import { useEffect, useState } from 'react';

const NAV_GROUPS = [
  {
    heading: 'Product',
    links: [
      { label: 'Overview', href: '#' },
      { label: 'Sound', href: '#sound' },
      { label: 'Colorways', href: '#colorways' },
      { label: 'Specifications', href: '#specifications' },
      { label: 'Reserve', href: '#reserve' },
    ],
  },
  {
    heading: 'Studio',
    links: [
      { label: 'Kanazawa', href: '#studio' },
      { label: 'Craftsmanship', href: '#studio' },
      { label: 'Editions', href: '#colorways' },
      { label: 'Press', href: '#' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Warranty', href: '#' },
      { label: 'Care & handling', href: '#' },
      { label: 'Contact', href: '#reserve' },
      { label: 'Repairs', href: '#' },
    ],
  },
];

export default function SiteFooter() {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 800);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer
      className="relative w-full"
      style={{
        backgroundColor: '#0C1220',
        borderTop: '1px solid rgba(255,255,255,0.10)',
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Floating back-to-top */}
      <button
        type="button"
        onClick={scrollTop}
        aria-label="Back to top"
        className="fixed z-50 whitespace-nowrap cursor-pointer flex items-center justify-center group"
        style={{
          right: '32px',
          bottom: '32px',
          width: '48px',
          height: '48px',
          borderRadius: '999px',
          background: 'rgba(122,168,238,0.14)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(122,168,238,0.35)',
          color: '#FFFFFF',
          opacity: showTop ? 1 : 0,
          transform: showTop
            ? 'translateY(0) scale(1)'
            : 'translateY(20px) scale(0.85)',
          pointerEvents: showTop ? 'auto' : 'none',
          transition:
            'opacity 380ms ease, transform 480ms cubic-bezier(0.22,1,0.36,1), background 220ms ease, border-color 220ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(122,168,238,0.28)';
          e.currentTarget.style.borderColor = 'rgba(122,168,238,0.65)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(122,168,238,0.14)';
          e.currentTarget.style.borderColor = 'rgba(122,168,238,0.35)';
        }}
      >
        <i
          className="ri-arrow-up-line transition-transform duration-300 group-hover:-translate-y-0.5"
          style={{ fontSize: '18px' }}
        />
      </button>

      <div
        className="mx-auto relative"
        style={{ maxWidth: '1280px', paddingLeft: '56px', paddingRight: '56px' }}
      >
        <div
          className="grid grid-cols-12 gap-8"
          style={{ paddingTop: '90px', paddingBottom: '80px' }}
        >
          {/* Brand + address */}
          <div className="col-span-12 lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span
                className="axiom-pulse-dot"
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#7AA8EE',
                  borderRadius: '2px',
                  display: 'inline-block',
                }}
              />
              <span
                className="text-white tracking-wider"
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                }}
              >
                AXIOM
              </span>
              <span
                style={{
                  color: 'rgba(255,255,255,0.35)',
                  fontSize: '13px',
                  fontWeight: 400,
                }}
              >
                / 07
              </span>
            </div>

            <p
              style={{
                color: '#9AA0AC',
                fontSize: '15px',
                lineHeight: 1.8,
                margin: 0,
                maxWidth: '380px',
                marginBottom: '36px',
              }}
            >
              A quiet audio studio in Kanazawa.
            </p>

            <div
              style={{
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: 1.7,
                letterSpacing: '0.005em',
              }}
            >
              Kanazawa · Japan
            </div>
          </div>

          {NAV_GROUPS.map((g) => (
            <div
              key={g.heading}
              className="col-span-6 md:col-span-4 lg:col-span-2 flex flex-col"
            >
              <div
                className="uppercase"
                style={{
                  color: '#7AA8EE',
                  fontSize: '10px',
                  letterSpacing: '0.32em',
                  fontWeight: 500,
                  marginBottom: '22px',
                }}
              >
                {g.heading}
              </div>
              <ul className="flex flex-col gap-3">
                {g.links.map((l) => {
                  const key = `${g.heading}-${l.label}`;
                  const isHovered = hoveredLink === key;
                  return (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        onMouseEnter={() => setHoveredLink(key)}
                        onMouseLeave={() => setHoveredLink(null)}
                        className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                        style={{
                          color: isHovered
                            ? '#FFFFFF'
                            : 'rgba(255,255,255,0.72)',
                          fontSize: '14.5px',
                          fontWeight: 500,
                          letterSpacing: '0.005em',
                          textDecoration: 'none',
                          transition:
                            'color 220ms ease, transform 380ms cubic-bezier(0.22,1,0.36,1)',
                          transform: isHovered
                            ? 'translateX(6px)'
                            : 'translateX(0)',
                        }}
                      >
                        <span
                          className="inline-block"
                          style={{
                            width: isHovered ? '18px' : '0px',
                            height: '1px',
                            background: '#7AA8EE',
                            transition:
                              'width 320ms cubic-bezier(0.22,1,0.36,1)',
                          }}
                        />
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Giant AXIOM word-mark — infinite marquee */}
      <div
        className="relative overflow-hidden select-none"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.10)',
          paddingTop: '48px',
          paddingBottom: '10px',
        }}
      >
        <div className="axiom-footer-marquee">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className="uppercase inline-flex items-center gap-8"
              style={{
                color: '#FFFFFF',
                fontSize: '18vw',
                fontWeight: 700,
                lineHeight: 0.85,
                letterSpacing: '-0.055em',
                opacity: 0.08,
                paddingRight: '6vw',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              AXIOM
              <span
                className="inline-block"
                style={{
                  width: '2vw',
                  height: '2vw',
                  minWidth: '18px',
                  minHeight: '18px',
                  backgroundColor: '#7AA8EE',
                  borderRadius: '2px',
                  opacity: 0.35,
                }}
              />
            </span>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="mx-auto"
        style={{ maxWidth: '1280px', paddingLeft: '56px', paddingRight: '56px' }}
      >
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{
            paddingTop: '28px',
            paddingBottom: '40px',
            borderTop: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <div className="flex items-center gap-6 flex-wrap">
            <span
              className="uppercase"
              style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '11px',
                letterSpacing: '0.28em',
                fontWeight: 500,
              }}
            >
              © 2026 AXIOM STUDIO
            </span>

          </div>

          <div className="flex items-center gap-5">
            {[
              { icon: 'ri-instagram-line', href: '#' },
              { icon: 'ri-twitter-x-line', href: '#' },
              { icon: 'ri-youtube-line', href: '#' },
              { icon: 'ri-vimeo-line', href: '#' },
            ].map((s) => (
              <a
                key={s.icon}
                href={s.href}
                className="w-9 h-9 flex items-center justify-center cursor-pointer group"
                style={{
                  color: 'rgba(255,255,255,0.65)',
                  transition: 'color 220ms ease, transform 320ms cubic-bezier(0.22,1,0.36,1)',
                }}
                aria-label="social link"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#7AA8EE';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className={s.icon} style={{ fontSize: '17px' }} />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-6">
            {['Privacy', 'Terms', 'Cookies'].map((l) => (
              <a
                key={l}
                href="#"
                className="whitespace-nowrap cursor-pointer"
                style={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '12.5px',
                  fontWeight: 500,
                  letterSpacing: '0.005em',
                  textDecoration: 'none',
                  transition: 'color 220ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
                }}
              >
                {l}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}